import axios from "axios";
import { jwtDecode } from "jwt-decode";

// Keycloak session helpers. Tokens live in localStorage; the client_id used
// at login is stored too, because a refresh must use the same client.

export const REQUIRED_ROLE = "Hire3xAdmin";
export const DEFAULT_CLIENT_ID = "vue-test-app";

const ACCESS_KEY = "accessToken";
const REFRESH_KEY = "refreshToken";
const CLIENT_KEY = "kcClientId";
const EXPIRY_SKEW_SECONDS = 30;

const keycloakUrl = () => (process.env.VUE_APP_KEYCLOAK_URL || "").replace(/\/+$/, "");

export const tokenEndpoint = () => `${keycloakUrl()}/protocol/openid-connect/token`;

export const getAccessToken = () => localStorage.getItem(ACCESS_KEY);
export const getRefreshToken = () => localStorage.getItem(REFRESH_KEY);
export const getClientId = () => localStorage.getItem(CLIENT_KEY) || DEFAULT_CLIENT_ID;

export function setTokens({ access_token, refresh_token }, clientId) {
  localStorage.setItem(ACCESS_KEY, access_token);
  if (refresh_token) localStorage.setItem(REFRESH_KEY, refresh_token);
  if (clientId) localStorage.setItem(CLIENT_KEY, clientId);
}

export function clearTokens() {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(CLIENT_KEY);
}

export function decode(token) {
  if (!token) return null;
  try {
    return jwtDecode(token);
  } catch (e) {
    return null;
  }
}

export function isExpired(token, skewSeconds = EXPIRY_SKEW_SECONDS) {
  const claims = decode(token);
  if (!claims || !claims.exp) return true;
  return claims.exp - skewSeconds <= Date.now() / 1000;
}

export function hasRole(role = REQUIRED_ROLE, token = getAccessToken()) {
  const claims = decode(token);
  const roles = (claims && claims.realm_access && claims.realm_access.roles) || [];
  return roles.includes(role);
}

// Logged in with a non-expired access token that carries the role.
export function isAuthenticated() {
  const token = getAccessToken();
  return !isExpired(token) && hasRole(REQUIRED_ROLE, token);
}

// The refresh token is usable (offline tokens may have no exp).
export function canRefresh() {
  const token = getRefreshToken();
  if (!token) return false;
  const claims = decode(token);
  if (!claims) return false;
  return !claims.exp || !isExpired(token, 0);
}

// Password grant used by the login page.
export async function login(username, password, clientId = DEFAULT_CLIENT_ID) {
  const body = new URLSearchParams({
    grant_type: "password",
    client_id: clientId,
    username,
    password,
  });
  const { data } = await axios.post(tokenEndpoint(), body, {
    headers: { "Content-Type": "application/x-www-form-urlencoded;charset=utf-8" },
  });
  if (!data || !data.access_token) throw new Error("No access token returned");
  setTokens(data, clientId);
  return data;
}

let refreshPromise = null;

// Single-flight refresh: concurrent callers share one request.
export function refreshTokens() {
  if (refreshPromise) return refreshPromise;
  const refreshToken = getRefreshToken();
  if (!refreshToken) return Promise.reject(new Error("No refresh token"));

  const body = new URLSearchParams({
    grant_type: "refresh_token",
    client_id: getClientId(),
    refresh_token: refreshToken,
  });
  refreshPromise = axios
    .post(tokenEndpoint(), body, {
      headers: { "Content-Type": "application/x-www-form-urlencoded;charset=utf-8" },
    })
    .then(({ data }) => {
      if (!data || !data.access_token) throw new Error("No access token returned");
      setTokens(data);
      return data.access_token;
    })
    .finally(() => {
      refreshPromise = null;
    });
  return refreshPromise;
}

// Returns a valid access token, refreshing it if needed; null when the
// session can't be recovered.
export async function ensureValidToken() {
  const token = getAccessToken();
  if (token && !isExpired(token)) return token;
  if (!canRefresh()) return null;
  try {
    return await refreshTokens();
  } catch (e) {
    return null;
  }
}
